<template>
  <div class="flex justify-center md:justify-end">
    <div>
      <div class="flex justify-end md:items-start items-center space-x-4 mt-7 md:mt-7">
        <div v-if="filterSideBarOpen" class="relative overflow-hidden">
          <ProductMobileFilterSideBar
            :is-visible="filterSideBarOpen"
            :filters="filterOptions"
            @on-side-bar-close="closeFilterSideBar"
            :results-number="filterStore.filtredTests.length"
          />
        </div>
        <div class="flex lg:hidden">
          <FilterFloatButton @clicked="openFilterSideBar()" :selectedFilters="filterStore.checked"></FilterFloatButton>
        </div>
        <div class="hidden lg:flex space-x-4">
          <div v-if="displayServiceFilter">
            <FilterSelect :placeholder="$t('common.service')" index-of="services" :selected-items="filterStore.service">
              <FilterCollectionMethods :Items="serviceTypeOptions" indexOf="service" />
            </FilterSelect>
          </div>
          <FilterSelect :options="sortOptions" :placeholder="`${$t('filter.sortBy')}`" @onClick="setSortOption" />
          <!-- Filter By category -->
          <div v-if="true || (filterOptions.categories.length > 1 && path[0] !== 'get-started')">
            <FilterSelect
              :placeholder="`${$t('filter.testCategory')}`"
              index-of="categories"
              :selected-items="filterStore.category"
            >
              <FilterCollectionMethods :Items="filterOptions.categories" indexOf="category" />
            </FilterSelect>
          </div>

          <!-- Filter By Infections  -->
          <FilterSelect :placeholder="`${$t('filter.testsFor')}`" :selected-items="filterStore.infection">
            <div class="flex flex-col -mt-5 md:mt-0 md:space-y-6 w-[270px]">
              <FilterInfection :infections="filterOptions.infections" />
            </div>
          </FilterSelect>

          <!-- Filter by Test Type  -->
          <FilterSelect
            :placeholder="`${$t('filter.testType')}`"
            index-of="test_types"
            v-if="path[0] !== 'get-started'"
            :selected-items="filterStore.test_type"
          >
            <FilterCollectionMethods :Items="filterOptions.test_types" indexOf="test_type" />
          </FilterSelect>

          <!-- Filter By Collection Methods  -->
          <FilterSelect
            :placeholder="`${$t('filter.collectionMethod')}`"
            index-of="collections"
            :selected-items="filterStore.collection"
          >
            <FilterCollectionMethods :Items="filterOptions.collection_methods" indexOf="collection" />
          </FilterSelect>

          <!-- Filter by Price Range  -->
          <FilterSelect :placeholder="`${$t('filter.priceRange')}`" :selected-items="filterStore.min_price">
            <div class="w-[300px]">
              <ProductFilterRangePrice isPanel :min="filterOptions.min_price" :max="filterOptions.max_price" />
            </div>
          </FilterSelect>
        </div>
        <!-- Filter By search. Name  -->
        <div class="relative">
          <BaseVSearchTextInput
            id="search-product"
            name="search-product"
            :label="`${$t('filter.searchText')}`"
            v-model="productSearch"
            @click.prevent="onClicked()"
            class="w-[204px]"
          />
          <button
            class="flex justify-center items-center bg-secondary text-white md:w-8 md:h-8 w-[40px] h-[40px] rounded-md absolute right-1 top-5 md:top-1 z-30"
          >
            <SearchIcon class="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useProductStore } from "@/stores/filter";
import { SearchIcon } from "@heroicons/vue/outline";
const filterStore = useProductStore();
const filterOptions = computed(() => filterStore.filterOptions);
const productSearch = ref("");
const filterSideBarOpen = ref(false);
const route = useRoute();
const mapStore = useMapStore();
const path = route.path.split("/").splice(2, 1);
watch(productSearch, (val) => {
  filterStore.setSearchValue(val);
});
const props = defineProps({
  isPriceClear: {
    type: Boolean,
  },
});
const sortOptions = computed(() => filterStore.getSortOptions);
const setSortOption = (item) => {
  filterStore.setSortOptionMobile(item);
};

const isServiceProductsLoading = ref(false);

const EVERYWHERE_SERVICE_SLUG = "everywhere-nurse-service";
const CLINIC_AND_INSTANT_SLUGS = ["clinic-test", "instant-test"];

const filterableServices = computed(() => {
  if (route.params.service === EVERYWHERE_SERVICE_SLUG) return [EVERYWHERE_SERVICE_SLUG];
  if (CLINIC_AND_INSTANT_SLUGS.includes(route.params.service)) return CLINIC_AND_INSTANT_SLUGS;
  return null;
});

const serviceTypeOptions = computed(() => {
  const services = filterOptions.value?.service_types ?? [];
  if (!filterableServices.value) return services;
  return services.filter((service) => filterableServices.value.includes(service.slug));
});

const displayServiceFilter = computed(() => {
  // Show the dropdown only when there is something selectable beyond the locked base
  // service. The everywhere flow has a single, non-dismissible service, so it stays hidden.
  return serviceTypeOptions.value.length > 1;
});
const serviceFilterSelectedItems = ref([
  {
    key: route.params.service,
  },
]);

async function handleChangeService(service) {
  const clinicSlug = route.query.slug || route.params.slug;
  if (!clinicSlug) return;

  const getStartedModalStore = useGetStartedModalStore();
  isServiceProductsLoading.value = true;
  try {
    /**
     * Fetch all tests related to a specific clinic.
     * @param clinic - Clinic Id from where we should fetch tests.
     */
    const fetchProducts = async (clinic) => {
      mapStore.setPanelTestsLoading(true);
      try {
        const { data: productsList } = await useFetchProductList({
          clinic,
          "service_type[0]": service.key,
        });
        mapStore.getTests(productsList.value?.data ?? []);

        const { data: servicesResults } = await useServiceListType();
        const services = servicesResults.value.data;
        const activeService = services.find((x) => x.slug === service.key);
        getStartedModalStore.setSelectedService(activeService);
      } finally {
        mapStore.setPanelTestsLoading(false);
      }
    };

    // Load and set new products list/states.
    await fetchProducts(mapStore.selectedClinic.id);
  } finally {
    isServiceProductsLoading.value = false;
  }

  // Navigate silenty
  /**
   * On get started page, use should not be able to change service because it was already selcted in second step
   * 
  const locale = switchBlogLocale();
  const url = new URL(
    `/${locale}/get-started/${route.params.section}/${service.key}`,
    window.location.origin
  );
  Object.entries(route.query).forEach((entry) =>
    url.searchParams.append(...entry)
  );
  window.history.replaceState(window.history.state, null, url.toString());
  **/
  serviceFilterSelectedItems.value = [
    {
      key: service.key,
    },
  ];

  filterStore.setService({
    slug: service.key,
    title: service.text,
  });
}

const onClicked = () => {
  if (productSearch.value == "") {
    filterStore.setSortOption(2);
  }
};
const openFilterSideBar = () => {
  document.body.classList.toggle("overflow-hidden");
  filterSideBarOpen.value = true;
};
const closeFilterSideBar = () => {
  document.body.classList.toggle("overflow-hidden");
  filterSideBarOpen.value = false;
};
</script>
